
import { Component, Prop, Vue } from 'vue-property-decorator';
import { DataTableHeader } from 'vuetify';
import { AxiosError } from 'axios';
import {
  FtpUser,
  FtpUserRepositoryInterface,
  FtpUserStatus,
  FtpUserUpdateDTO,
  FtpUserWithLogs,
} from '@/services/FtpUser/interfaces';
import { ServerInterface, ServerRepositoryInterface } from '@/services/Server/Interfaces';
import { Website } from '@/services/Website/interfaces';
import { SystemUserInterface, SystemUserRepositoryInterface } from '@/services/SystemUsers/interfaces';
import EditFtpUserDialog from '@/components/dialogs/ftp-users/EditFtpUserDialog.vue';
import DeleteFtpUserDialog from '@/components/dialogs/ftp-users/DeleteFtpUserDialog.vue';
import CreateFtpUserDialog from '@/components/dialogs/ftp-users/CreateFtpUserDialog.vue';
import generateConnectUrl from '@/views/pages/ftp-users/utils';

@Component({
  inject: [
    'ftpUserRepository',
    'serverRepository',
    'systemUserRepository',
  ],
  components: {
    EditFtpUserDialog,
    DeleteFtpUserDialog,
    CreateFtpUserDialog,
  },
})
export default class WebsiteFtpUsersPanel extends Vue {
  @Prop({ required: true }) private readonly website!: Website;

  private ftpUserRepository!: FtpUserRepositoryInterface;
  private serverRepository!: ServerRepositoryInterface;
  private systemUserRepository!: SystemUserRepositoryInterface;

  private loading: boolean = true;
  private ftpUsers: FtpUser[] = [];
  private serversMap: { [key: string]: ServerInterface } = {};
  private systemUser: SystemUserInterface | null = null;

  private headers: DataTableHeader[] = [
    { text: 'ID', value: 'id' },
    { text: 'Username', value: 'username' },
    { text: 'Account User', value: 'accountName' },
    { text: 'Website', value: 'websiteId' },
    { text: 'Server', value: 'server' },
    { text: 'Directory', value: 'homeDir' },
    { text: 'Status', value: 'status' },
    { text: 'Actions', value: 'actions' },
  ];

  private editFtpUserDialogData: {
    ftpUser: FtpUserWithLogs;
    server: ServerInterface;
  } | false = false;
  private editFtpUserDialog: string = '';

  private createFtpUserDialog: string = '';

  private deleteFtpUserDialog: string = '';
  private ftpUserForDeleteDialog: FtpUser | false = false;

  public get systemUserLabel(): string {
    if (this.systemUser?.name) {
      return `${this.systemUser.name} (#${this.systemUser.id})`;
    }

    const unixUserId = this.website.details?.unixUserId;
    return unixUserId ? `#${unixUserId}` : 'N/A';
  }

  public async created(): Promise<void> {
    try {
      await this.loadFtpUsers();
    } catch (err) {
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  private getStatus(ftpUser: FtpUser): string {
    return FtpUserStatus[ftpUser.status];
  }

  private async loadFtpUsers(): Promise<void> {
    const unixUserId = this.website.details?.unixUserId;
    if (!unixUserId) {
      this.ftpUsers = [];
      this.systemUser = null;
      return;
    }

    this.systemUser = await this.systemUserRepository.findSystemUserById(unixUserId);
    // Same search the FTP users page uses: ?page=1&search=<systemUser.name>
    const search = this.systemUser.name;
    if (!search) {
      this.ftpUsers = [];
      return;
    }

    const allFtpUsers: FtpUser[] = [];
    let page = 1;
    let total = Number.POSITIVE_INFINITY;

    while (allFtpUsers.length < total) {
      const searchResult = await this.ftpUserRepository.searchFtpUsers(page, search);
      total = searchResult.total;
      allFtpUsers.push(...searchResult.ftpUsers);

      if (!searchResult.ftpUsers.length || !searchResult.next) {
        break;
      }

      page += 1;
    }

    this.ftpUsers = allFtpUsers;

    const serverIds = [...new Set(this.ftpUsers.map((ftpUser) => ftpUser.server))];
    if (serverIds.length) {
      const servers = await this.serverRepository.getServersByIds(serverIds);
      servers.forEach((server) => {
        this.serversMap[server._id] = server;
      });
    }
  }

  private createFtpUser(): void {
    this.createFtpUserDialog = 'create-ftp-user-dialog';
  }

  private async onFtpUserCreated(): Promise<void> {
    try {
      this.loading = true;
      await this.loadFtpUsers();
    } catch (err) {
      this.handleError(err);
    } finally {
      this.loading = false;
    }
  }

  private async editFtpUser(ftpUser: FtpUser): Promise<void> {
    try {
      const updateData: FtpUserUpdateDTO = {
        password: ftpUser.password,
      };
      await this.ftpUserRepository.updateFtpUser(ftpUser.id, updateData);
      this.$successDialog.show('FTP User update started!');
    } catch (err) {
      this.handleError(err);
    }
  }

  private async deleteFtpUser(ftpUser: FtpUser): Promise<void> {
    try {
      await this.ftpUserRepository.deleteFtpUser(ftpUser.id);
      this.ftpUsers = this.ftpUsers.filter((user) => user.id !== ftpUser.id);
      this.$successDialog.show('FTP User deletion started!');
    } catch (err) {
      this.handleError(err);
    }
  }

  private async redirectToConnectPage(id: number): Promise<void> {
    try {
      const ftpUser = await this.ftpUserRepository.findOneById(id);
      if (!ftpUser) {
        return;
      }

      const hostname: string | undefined = this.serversMap[ftpUser.server]?.hostname;
      if (!hostname) {
        this.$errorDialog.show('Unknown server!');
        return;
      }

      const jwtToken = this.$store.getters.jwt.replace('Bearer ', '');

      document.cookie = `jwt=${jwtToken}; path=/; SameSite=Lax;`;
      document.cookie = `ftpUserId=${ftpUser.id}; path=/; SameSite=Lax;`;
      document.cookie = `websiteId=${ftpUser.websiteId}; path=/; SameSite=Lax;`;
      document.cookie = `username=${ftpUser.username}; path=/; SameSite=Lax;`;

      window.open(generateConnectUrl(), '_blank');
    } catch (err) {
      this.handleError(err);
    }
  }

  private async showEditDialog(id: number): Promise<void> {
    try {
      const ftpUser = await this.ftpUserRepository.findOneById(id);
      if (!ftpUser) {
        return;
      }

      this.editFtpUserDialogData = {
        ftpUser,
        server: this.serversMap[ftpUser.server],
      };
      this.editFtpUserDialog = 'edit-ftp-user-dialog';
    } catch (err) {
      this.handleError(err);
    }
  }

  private showDeleteDialog(id: number): void {
    const ftpUser = this.ftpUsers.find((user) => user.id === id);
    if (!ftpUser) {
      return;
    }

    this.ftpUserForDeleteDialog = ftpUser;
    this.deleteFtpUserDialog = 'delete-ftp-user-dialog';
  }

  private handleError(err: unknown): void {
    const axiosErr = err as AxiosError;
    if (axiosErr.isAxiosError) {
      this.$errorDialog.show(axiosErr.response?.data?.message || axiosErr.message);
    } else if (err instanceof Error) {
      this.$errorDialog.show(err.message);
    }
  }
}
